import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DialogData, DialogResult, Titles} from '@fgpp-ui/grid';
import { BasicColumn } from '@fgpp-ui/grid';
import { ColumnSettingsModalComponent } from '@fgpp-ui/grid';
import { ColumnSettingsService as LibColumnSettingsService } from '@fgpp-ui/grid';
import {DialogDataResponse} from "../../../shared/fn-ui-export/models/dialog-data-response.model";
import {BehaviorSubject} from "rxjs";
import {NavigationService} from "../../../core/navigation/services/navigation.service";
import {CreatePaymentItem} from "../../../core/navigation/sitemap/models";
import {
  QUICK_ACTION_BUTTON_DEFAULT_LIST,
  QUICK_ACTION_BUTTON_USER_PERMISSION_LIST
} from "../../models/consts/quick-action-button.const";
import {FeatureName} from "../../../core/feature-flags/models/feature-name.enum";
import {FeatureFlagsService} from "../../../core/feature-flags/services/feature-flags.service";
import {QuickActions} from "../../models/enums/quick-actions.enum";
import {QuickActionsApiService} from "./quick-actions-api.service";
import {UserQuickActionModalLists} from "../../models/user-quick-action-modal-lists.model";

@Injectable({
  providedIn: 'root'
})
export class QuickActionsService  {

  maxColumnSelection = 3;
  minColumnSelection = 3;
  public fetchingDataInProgress$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  userActionsList : Array<QuickActions>;

  constructor(
    protected translateService: TranslateService,
    protected columnSettingsService: LibColumnSettingsService<BasicColumn>,
    protected navigationService: NavigationService,
    protected featureFlagsService: FeatureFlagsService,
    protected quickActionsApiService: QuickActionsApiService
  ) { }
  private get quickActionsTitles(): Titles {
    return {
      columnSettings: this.translateService.instant('quick_actions.mainTitle'),
      available: this.translateService.instant('column_settings.availableColumnTitle'),
      selected: this.translateService.instant('column_settings.selectedColumnTitle'),
      itemsSelected: this.translateService.instant('column_settings.items_selected'),
      max: this.translateService.instant('column_settings.max'),
      maxSelection: this.translateService.instant('column_setting.max_items_displayed', { maxAllowedColumnsInGrid: this.maxColumnSelection }),
      moveRight: this.translateService.instant('column_setting.move_right'),
      moveLeft: this.translateService.instant('column_setting.move_left'),
      removeAll: this.translateService.instant('main_window.refine_remove_all'),
      moveTop: this.translateService.instant('column_setting.move_top'),
      moveUp: this.translateService.instant('column_setting.move_up'),
      moveDown: this.translateService.instant('column_setting.move_down'),
      moveBottom: this.translateService.instant('column_setting.move_bottom'),
      restore: this.translateService.instant('column_setting.restore_default_button'),
      save: this.translateService.instant('general.save'),
      cancel: this.translateService.instant('general.cancel'),
      search: this.translateService.instant('column_settings.search'),
      saveGlobally: this.translateService.instant('column_setting.save-globally'),
    };
  }



  openDialog(availableList: Array<any>, selectedList: Array<any>, shouldShowApplyAll:boolean): MatDialogRef<ColumnSettingsModalComponent<BasicColumn>> {
    const data =  this.getDialogData(availableList, selectedList,shouldShowApplyAll);
    return this.columnSettingsService.open(data);
  }

  private getDialogData(availableList: Array<any>, selectedList: Array<any>, shouldShowApplyAll:boolean): DialogData<BasicColumn> {
    let convertedAvailableList: Array<BasicColumn> = this.getBasicColumn(availableList);
    let convertedSelectList: Array<BasicColumn> = this.getBasicColumn(selectedList);
    const data: DialogData<BasicColumn> = {
      availableColumns: convertedAvailableList,
      selectedColumns: convertedSelectList,
      maxSelection: this.maxColumnSelection,
      minSelection: this.minColumnSelection,
      titles: this.quickActionsTitles,
      dualListboxContainerClass: 'fa-dual-listbox-container',
      showApplyAll: shouldShowApplyAll
    };
    return data;
  }

  handleQuickActionData(result: DialogDataResponse | string):Promise<void>{
    let promise;
    if (  typeof result === 'object') {
      promise = this.quickActionsApiService.saveUserQuickActions(result.selectedColumns);
    } else if (result === DialogResult.RESTORE) {
      promise =  this.quickActionsApiService.deleteUserQuickActions();
    }else if (result === null || result === undefined) {
      throw new Error('empty data');
    }
    if (promise) {
      this.fetchingDataInProgress$.next(true);
      let res = typeof result == 'string' ? result : result.selectedColumns;
      return promise.then(() => this.onUserActionChange(res).catch(() => {
        this.fetchingDataInProgress$.next(false);
      }));
    }
  }

  onUserActionChange(columns:Array<BasicColumn> | string){
      if(typeof columns === "string" ){//restore
        this.userActionsList =[...QUICK_ACTION_BUTTON_DEFAULT_LIST];
      }else{
        this.userActionsList =this.convertToUserActionList(columns)
      }
    return new Promise(resolve => resolve(this.userActionsList));
  }

  async getQuickActionModalLists(): Promise<UserQuickActionModalLists>{
    let available = this.getUserQuickActionListByPermission();
    let selected = await this.getSelectedQuickActionList(available);
    return {
      selected:selected,
      available: available
    }
  }

  protected getBasicColumn(actionsList : Array<string>): BasicColumn[]{
    let list = new Array<BasicColumn>();

    for(let str of actionsList){
      let displayName = this.translateService.instant('messages_center.landing_page.change_quick_actions.'+str);
      list.push({
        name: str,
        displayName: displayName
      })
    }
    return list
  }

   convertToUserActionList(basicColumns : Array<BasicColumn>): Array<QuickActions> {
    let arr = [];
    for(let col of basicColumns ){
      arr.push(col.name)
    }
   return arr;
  }


   protected getUserQuickActionListByPermission(): Array<QuickActions>{
     let userPermissionList: Array<CreatePaymentItem> = this.navigationService.getCreatePaymentItems();
     const permissionActions = userPermissionList.map(permission => permission.action);
     let quickActionList = QUICK_ACTION_BUTTON_USER_PERMISSION_LIST.filter(action=>permissionActions.includes(action));
     let isFxDisabled = this.featureFlagsService.isFeatureOff(FeatureName.fxFeature);
     if(isFxDisabled){
       quickActionList= quickActionList.filter(action => action!== QuickActions.CREATE_FX_PAYMENT)
     }else{
       quickActionList.push(QuickActions.CREATE_FX_PAYMENT);
     }
     return [...QUICK_ACTION_BUTTON_DEFAULT_LIST,...quickActionList];
  }

  protected async getSelectedQuickActionList(userAllowedQuickActions){
    if(this.userActionsList){
      return this.userActionsList
    }
    const list =  await this.quickActionsApiService.getUserQuickActions().toPromise();
      if(!list){
        let res = [...QUICK_ACTION_BUTTON_DEFAULT_LIST];
        return res;
      }
      let filtered = list.filter(item=>userAllowedQuickActions.includes(item))
      if(!filtered.length){
        filtered =[...QUICK_ACTION_BUTTON_DEFAULT_LIST];
      }
      this.userActionsList = filtered
      return this.userActionsList;
  }

   restoreDefault(){
    return this.quickActionsApiService.deleteUserQuickActions();
  }

}
